import { isEmpty, isNil } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import { Icon, IconName } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBSearchBar } from '@shared/components/searchBar';
import { bounceIn, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MenuItem } from './MenuItem';
import { MenuLoading } from './MenuLoading';
import { MenuScrollable } from './MenuScrollable';
import { MenuSection } from './MenuSection';
import styles from './MenuOptions.module.css';

/**
 * Menu.Options / Menu.SearchableOptions
 *
 * Renders a generic list of selectable `Menu.Item` rows from an array of items.
 *
 * Selected items are marked with a checkmark via `isItemSelected`. If `multiple`
 * is set, rows show checked/empty checkboxes instead; if `radio` is set, a
 * single-select list shows filled/empty radio buttons. If `isItemSelected` is
 * not provided, no markers will be shown.
 *
 * Rows with markers are `menuitemcheckbox` (with `multiple`) or `menuitemradio`
 * rows whose `aria-checked` follows `isItemSelected`, so the selection reaches
 * assistive tech and not just the marker.
 *
 * Rows that need a body of their own pass `getItemContent`; Use `renderItem` if
 * you need to customize rendering of the whole row.
 *
 * Lists that arrive over the network pass `loading` while they fetch and can
 * append a `footer` — a load-more button, a create row — that scrolls with
 * the rows.
 *
 * `Menu.SearchableOptions` wraps `Menu.Options` with a search bar that filters
 * items client-side by label. Use it when the option list is long enough that
 * users benefit from filtering. Pass `filter` and `setFilter` to own the search
 * text instead — the menu then renders `options` as given, so the caller can
 * attach a network query and hand back its results.
 */

const SEARCH_THRESHOLD = 10;

interface MenuOptionsProps<T> {
  options: T[];
  getItemKey: (item: T) => string;
  getItemLabel: (item: T) => string;
  getItemIcon?: (item: T) => IconName | undefined | 'none';
  /** Leading content for a row that carries an image rather than an icon — an avatar, a thumbnail. */
  getItemStartContent?: (item: T) => ReactNode;
  onSelect?: (item: T) => void;
  isItemSelected?: (item: T) => boolean;
  isItemDisabled?: (item: T) => boolean;
  /**
   * Custom body for a row — a second line, a pill, a tag swatch — rendered as the item’s children
   * in place of its label. The menu still owns key, icon, click, marker and disabled state.
   */
  getItemContent?: (item: T) => ReactNode;
  /**
   * The whole row, for rows that aren’t a `Menu.Item` at all — unlike `getItemContent` the callback
   * owns the key, click, icon, marker and disabled state, and every prop driving them is ignored.
   */
  renderItem?: (item: T) => ReactNode;
  /** Marks rows with a checkbox instead of a checkmark, for menus that allow multiple selection. */
  multiple?: boolean;
  /**
   * Marks rows with a radio button instead of a checkmark, for single-select menus that stand in
   * for a form control. Ignored when `multiple` is set.
   */
  radio?: boolean;
  /**
   * Shown in place of the rows when there are none — a menu that opens onto nothing collapses to a
   * sliver that reads as failed-to-open. Defaults to "No results"; pass one to say something better.
   */
  emptyLabel?: string;
  /** Shows a spinner in place of the rows — and of the empty state — while the list is fetched. */
  loading?: boolean;
  /** Rendered after the last row, inside the scroll area — a load-more button, a create row. */
  footer?: ReactNode;
}

export function MenuOptions<T>({
  options,
  getItemKey,
  onSelect,
  isItemSelected,
  getItemLabel,
  getItemIcon,
  getItemStartContent,
  getItemContent,
  isItemDisabled,
  renderItem,
  multiple = false,
  radio = false,
  emptyLabel,
  loading = false,
  footer,
}: MenuOptionsProps<T>) {
  const Marker = getMarker(multiple, radio);
  const role = getItemRole(multiple, !isNil(isItemSelected));

  if (loading) {
    return <MenuLoading />;
  }

  if (isEmpty(options)) {
    return (
      <>
        <EmptyOptions label={emptyLabel} />
        {footer}
      </>
    );
  }

  return (
    <MenuScrollable showScrollIndicators>
      {isNil(renderItem) &&
        options.map(option => {
          const label = getItemLabel(option);
          const content = getItemContent?.(option);

          return (
            <MenuItem
              key={getItemKey?.(option)}
              icon={getItemIcon?.(option)}
              startContent={getItemStartContent?.(option)}
              // `RowContent` renders `label` *or* `children`, so a row with its
              // own content should not pass label
              label={isNil(content) ? label : undefined}
              aria-label={label}
              role={role}
              aria-checked={isItemSelected?.(option)}
              onClick={() => onSelect?.(option)}
              disabled={isItemDisabled?.(option)}
              endContent={isItemSelected && <Marker isSelected={isItemSelected(option)} />}
            >
              {content}
            </MenuItem>
          );
        })}
      {!isNil(renderItem) && options.map(renderItem)}
      {footer}
    </MenuScrollable>
  );
}

export function SelectedMarker({ isSelected }: { isSelected: boolean }) {
  const key = isSelected ? 'selected' : 'not-selected';
  return (
    <View
      key={key}
      center
      style={{ width: grid(1.5) }}
      className={[styles.selectedMarker, isSelected && bounceIn]}
    >
      {isSelected && <Icon name="check-box" gridSize={4} />}
    </View>
  );
}

export function CheckboxMarker({ isSelected }: { isSelected: boolean }) {
  const key = isSelected ? 'selected' : 'not-selected';
  const icon = isSelected ? 'check-box' : 'check-box-empty';
  return (
    <View key={key} center className={[styles.selectedMarker, isSelected && bounceIn]}>
      <Icon name={icon} gridSize={4} />
    </View>
  );
}

export function RadioMarker({ isSelected }: { isSelected: boolean }) {
  const key = isSelected ? 'selected' : 'not-selected';
  const icon = isSelected ? 'radio-button' : 'unfilled-circle';
  return (
    <View key={key} center className={[styles.selectedMarker, isSelected && bounceIn]}>
      <Icon name={icon} gridSize={4.5} />
    </View>
  );
}

function getItemRole(multiple: boolean, isSelectable: boolean) {
  if (!isSelectable) {
    return 'menuitem';
  }
  if (multiple) {
    return 'menuitemcheckbox';
  }
  return 'menuitemradio';
}

function getMarker(multiple: boolean, radio: boolean) {
  if (multiple) {
    return CheckboxMarker;
  }
  if (radio) {
    return RadioMarker;
  }
  return SelectedMarker;
}

interface SearchableOptionsProps<T> extends MenuOptionsProps<T> {
  title?: string;
  /** Sits across from `title`, at the end of the header line. */
  titleEnd?: ReactNode;
  /**
   * The search text, when the caller owns it — for a list filtered by a network query rather than
   * by label. Pass both or neither: with these set the menu skips its own filtering, renders
   * `options` as given and always shows the search bar, since a result count says nothing about
   * whether the full set is worth searching.
   */
  filter?: string;
  setFilter?: (filter: string) => void;
  /** Placeholder for the search bar; defaults to "Search…". */
  searchPlaceholder?: string;
}

export function SearchableOptions<T>({
  title,
  titleEnd,
  options,
  onSelect,
  isItemSelected,
  getItemKey,
  getItemLabel,
  getItemIcon,
  getItemStartContent,
  getItemContent,
  isItemDisabled,
  renderItem,
  multiple,
  radio,
  emptyLabel,
  loading,
  footer,
  filter: controlledFilter,
  setFilter: setControlledFilter,
  searchPlaceholder,
}: SearchableOptionsProps<T>) {
  const [localFilter, setLocalFilter] = useState('');
  const isControlled = !isNil(controlledFilter) && !isNil(setControlledFilter);
  const filter = isControlled ? controlledFilter : localFilter;
  const setFilter = isControlled ? setControlledFilter : setLocalFilter;

  const filteredOptions = useMemo(() => {
    if (isControlled) {
      return options;
    }
    return options.filter(option => {
      const label = getItemLabel(option);
      return label.toLowerCase().includes(filter.toLowerCase());
    });
  }, [isControlled, filter, options, getItemLabel]);

  const showSearch = isControlled || options.length > SEARCH_THRESHOLD;
  const showHeader = !isNil(title) || !isNil(titleEnd) || showSearch;

  return (
    <>
      {showHeader && (
        <MenuSection title={title} titleEnd={titleEnd}>
          {showSearch && (
            <MBSearchBar value={filter} onChangeText={setFilter} placeholder={searchPlaceholder} />
          )}
        </MenuSection>
      )}
      <MenuOptions
        options={filteredOptions}
        onSelect={onSelect}
        isItemSelected={isItemSelected}
        getItemKey={getItemKey}
        getItemLabel={getItemLabel}
        getItemIcon={getItemIcon}
        getItemStartContent={getItemStartContent}
        getItemContent={getItemContent}
        isItemDisabled={isItemDisabled}
        renderItem={renderItem}
        multiple={multiple}
        radio={radio}
        emptyLabel={emptyLabel}
        loading={loading}
        footer={footer}
      />
    </>
  );
}

/**
 * Menu.EmptyOptions
 *
 * The row a menu shows in place of its options when it has none — a menu that opens onto nothing
 * collapses to a sliver that reads as failed-to-open. `Menu.Options` renders this itself; use it
 * directly in a menu that keeps its own list, so the two read the same.
 */

interface EmptyOptionsProps {
  /** Defaults to "No results"; pass one where the menu can say something more useful. */
  label?: string;
  icon?: IconName;
}

export function EmptyOptions({ label, icon = 'exclamation-outline' }: EmptyOptionsProps) {
  return (
    <View center className={styles.emptyOptions}>
      <View row centerVertically gap={0.5}>
        <Icon name={icon} gridSize={4} />
        <Text inactive balance>
          {label ?? i18n('common.empty')}
        </Text>
      </View>
    </View>
  );
}
